import { BoxProps, Chip, Stack } from "@mui/material";
import { uniq } from "lodash-es";
import { useMemo } from "react";

import { EntityHelper } from "@/common/helpers/entity";
import { useMyTenantAffiliation } from "@/common/hooks/entity/tenant/useMyTenantAffiliation";
import { BaseEntityDtoAny, EntityAffiliationInfo } from "@/common/ts/entity";

import { AppChipProps } from "../../Chip/AppChip";
import FieldValue from "../../Form/Display/FieldValue";
import AppIcon from "../../Icons/AppIcon";
import AppPopover from "../../Popover/AppPopover";
import AppPopoverContent from "../../Popover/AppPopoverContent";
import AppTypography from "../../Text/AppTypography";
import DepartmentLink from "../Department/DepartmentLink";
import LocationLink from "../Location/LocationLink";
import TenantLink from "../Tenant/TenantLink";

const defaultDisplayProps = {
  tenant: true,
  ownerTenant: true,
  departments: true,
  locations: true,
};
export type AffiliationDisplayProps = typeof defaultDisplayProps;

export type AffiliationInfoDisplayVariant = "normal" | "compact";

export interface EntityAffiliationInfoDisplayProps {
  variant?: AffiliationInfoDisplayVariant;
  entity?: Nil<BaseEntityDtoAny>;
  affiliationInfo?: Nil<EntityAffiliationInfo>;
  /** Entity tenant. */
  tenantId: string | null | undefined;
  ownerTenantId?: string | null;
  departmentIds?: string[] | null;
  locationIds?: string[] | null;
  departmentId?: string | null;
  locationId?: string | null;
  /** @deprecated Use `displayProps`. */
  withoutTenant?: boolean;
  displayProps?: Partial<AffiliationDisplayProps>;
  chipProps?: Partial<AppChipProps>;
  sx?: BoxProps["sx"];
}

export default function EntityAffiliationInfoDisplay({
  variant = "normal",
  entity,
  affiliationInfo,
  tenantId,
  ownerTenantId,
  departmentId,
  departmentIds,
  locationId,
  locationIds,
  withoutTenant = false,
  displayProps,
  chipProps,
  sx,
}: EntityAffiliationInfoDisplayProps) {
  const affiliationInfoComputed = affiliationInfo || EntityHelper.getAffiliationInfo(entity);
  const tenantIdComputed = tenantId || affiliationInfoComputed?.tenantId;
  const ownerTenantIdComputed = ownerTenantId;

  const myTenantAffiliation = useMyTenantAffiliation({ tenantId: tenantIdComputed });

  const departmentIdsComputed = useMemo(
    () =>
      uniq(
        [
          departmentId,
          ...(departmentIds || []),
          ...(affiliationInfoComputed?.departmentIds || []),
        ].filter(Boolean),
      ),
    [departmentId, departmentIds, affiliationInfoComputed?.departmentIds],
  );
  const locationIdsComputed = useMemo(
    () =>
      uniq(
        [
          locationId,
          ...(locationIds || []),
          ...(affiliationInfoComputed?.locationIds || []),
        ].filter(Boolean),
      ),
    [locationId, locationIds, affiliationInfoComputed?.locationIds],
  );

  const displayPropsExternal = {
    ...defaultDisplayProps,
    ...displayProps,
  };
  const displayPropsInternal = {
    tenant: !withoutTenant,
    ownerTenant: !!ownerTenantIdComputed && !withoutTenant,
    departments: myTenantAffiliation.isMyTenant,
    locations: myTenantAffiliation.isMyTenant,
  };
  const displayPropsComputed = {
    tenant: displayPropsExternal.tenant && displayPropsInternal.tenant,
    ownerTenant: displayPropsExternal.ownerTenant && displayPropsInternal.ownerTenant,
    departments: displayPropsExternal.departments && displayPropsInternal.departments,
    locations: displayPropsExternal.locations && displayPropsInternal.locations,
  };
  const isDisplayAny =
    displayPropsComputed.tenant ||
    displayPropsComputed.ownerTenant ||
    displayPropsComputed.departments ||
    displayPropsComputed.locations;
  if (!isDisplayAny) {
    return null;
  }

  return (
    <>
      {/* Chip */}
      {variant === "compact" && (
        <AppPopover
          hoverBehavior={{
            closeBehavior: "onTriggerOrContentMouseLeave",
          }}
          trigger={
            <Stack sx={sx} direction='row' gap={1}>
              {displayPropsComputed.departments && (
                <Chip
                  variant='filled'
                  color='default'
                  size='medium'
                  {...chipProps}
                  label={
                    <Stack direction={"row"} gap={0.5} alignItems={"center"}>
                      <AppIcon of='department' fontSize='medium' />
                      <AppTypography>{departmentIdsComputed.length}</AppTypography>
                    </Stack>
                  }
                />
              )}

              {displayPropsComputed.locations && (
                <Chip
                  variant='filled'
                  color='default'
                  size='medium'
                  {...chipProps}
                  label={
                    <Stack direction={"row"} gap={0.5} alignItems={"center"}>
                      <AppIcon of='location' fontSize='medium' />
                      <AppTypography>{locationIdsComputed.length}</AppTypography>
                    </Stack>
                  }
                />
              )}
            </Stack>
          }
        >
          <AppPopoverContent sx={{ width: 400, maxWidth: 400 }}>
            <Stack spacing={1}>
              {displayPropsComputed.tenant && (
                <FieldValue
                  variant='standard'
                  labelIcon={<AppIcon of='company' inText />}
                  direction='column'
                  label='Company'
                  isEmpty={!tenantIdComputed}
                >
                  <TenantLink entity={undefined} entityId={tenantIdComputed} />
                </FieldValue>
              )}

              {displayPropsComputed.ownerTenant && (
                <FieldValue
                  variant='standard'
                  labelIcon={<AppIcon of='company' inText />}
                  direction='column'
                  label='Owner company'
                  isEmpty={!ownerTenantIdComputed}
                >
                  <TenantLink entity={undefined} entityId={ownerTenantIdComputed} />
                </FieldValue>
              )}

              {displayPropsComputed.departments && (
                <FieldValue
                  variant='standard'
                  labelIcon={<AppIcon of='departments' inText />}
                  direction='column'
                  label='Departments'
                  isEmpty={departmentIdsComputed.length === 0}
                >
                  {departmentIdsComputed.map((id) => (
                    <DepartmentLink
                      key={id}
                      entity={undefined}
                      entityId={id}
                      sx={{ mr: "10px !important" }}
                    />
                  ))}
                </FieldValue>
              )}

              {displayPropsComputed.locations && (
                <FieldValue
                  variant='standard'
                  labelIcon={<AppIcon of='location' inText />}
                  direction='column'
                  label='Locations'
                  isEmpty={locationIdsComputed.length === 0}
                >
                  {locationIdsComputed.map((id) => (
                    <LocationLink
                      key={id}
                      entity={undefined}
                      entityId={id}
                      sx={{ mr: "10px !important" }}
                    />
                  ))}
                </FieldValue>
              )}
            </Stack>
          </AppPopoverContent>
        </AppPopover>
      )}

      {variant === "normal" && (
        <Stack spacing={1} sx={sx}>
          {(displayPropsComputed.tenant || displayPropsComputed.ownerTenant) && (
            <Stack direction={{ xxs: "column", md: "row" }} gap={1} sx={{ width: "100%" }}>
              {displayPropsComputed.tenant && (
                <Stack sx={{ flex: 1 }}>
                  <FieldValue
                    variant='standard'
                    labelIcon={<AppIcon of='company' inText />}
                    direction='column'
                    label='Company'
                    isEmpty={!tenantIdComputed}
                  >
                    <TenantLink entity={undefined} entityId={tenantIdComputed} />
                  </FieldValue>
                </Stack>
              )}

              {displayPropsComputed.ownerTenant && (
                <Stack sx={{ flex: 1 }}>
                  <FieldValue
                    variant='standard'
                    labelIcon={<AppIcon of='company' inText />}
                    direction='column'
                    label='Owner company'
                    isEmpty={!ownerTenantIdComputed}
                  >
                    <TenantLink entity={undefined} entityId={ownerTenantIdComputed} />
                  </FieldValue>
                </Stack>
              )}
            </Stack>
          )}

          {displayPropsComputed.departments && (
            <FieldValue
              variant='standard'
              labelIcon={<AppIcon of='departments' inText />}
              direction='column'
              label='Departments'
              isEmpty={departmentIdsComputed.length === 0}
            >
              {departmentIdsComputed.map((id) => (
                <DepartmentLink
                  key={id}
                  entity={undefined}
                  entityId={id}
                  sx={{ mr: "10px !important" }}
                />
              ))}
            </FieldValue>
          )}

          {displayPropsComputed.locations && (
            <FieldValue
              variant='standard'
              labelIcon={<AppIcon of='location' inText />}
              direction='column'
              label='Locations'
              isEmpty={locationIdsComputed.length === 0}
            >
              {locationIdsComputed.map((id) => (
                <LocationLink
                  key={id}
                  entity={undefined}
                  entityId={id}
                  sx={{ mr: "10px !important" }}
                />
              ))}
            </FieldValue>
          )}
        </Stack>
      )}
    </>
  );
}
